import queryString from 'query-string';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import {
  useMainDispatch,
  useMainSelector,
  useUserEnvironment,
} from 'modules/main/store';
import {
  clearSearchLocation,
  setPageIndex,
  setSearchFilter,
  setSearchQuery,
  setUserInitiatedSearch,
  updateSearchLocation,
} from 'modules/main/store/ducks/mainSearch';
import { showModal } from 'modules/main/store/ducks/modal';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import {
  DisplayedType,
  EMPTY_SEARCH_LOCATION,
  listingTypesBySection,
} from 'utils/constants';
import { listingSearchHome } from 'utils/searchHomePages';
import { truncateToUTF8Bytes } from 'utils/string';
import { getFirstParam } from 'utils/url';

export function useMainSearchMenu(
  searchFrom?: string,
  searchMode?: boolean,
  defaultSearchType?: DefaultSearchType,
) {
  const dispatch = useMainDispatch();
  const {
    mainSearch: reduxMainSearch,
    query: reduxQuery,
    type: reduxListingType,
  } = useMainSelector((state) => ({
    mainSearch: state.mainSearch,
    query: state.mainSearch ? state.mainSearch.query : '',
    type: state.mainSearch?.filters?.type,
  }));
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { user } = useUserEnvironment();
  const [clearing, setClearing] = useState(false);
  const [listingTypes] = useState<Array<DisplayedType>>(
    listingTypesBySection.MAIN,
  );
  const [listingType, setListingType] = useState<
    ListingType | DefaultSearchType
  >(reduxListingType || defaultSearchType || listingTypes[0]);
  const [query, setQuery] = useState(
    truncateToUTF8Bytes(reduxQuery || getFirstParam(location, 'q') || '', 512),
  );
  const [enqueueSearch, setEnqueueSearch] = useState([false, null]);
  const [searchLocation, setSearchLocation] = useState<
    SearchLocation | null | undefined
  >(reduxMainSearch.searchLocation);
  useEffect(() => {
    if (defaultSearchType) {
      setListingType(defaultSearchType);
    }
  }, [defaultSearchType]);
  useEffect(() => {
    setSearchLocation(reduxMainSearch.searchLocation);
  }, [reduxMainSearch.searchLocation]);

  useEffect(() => {
    if (navigationType === 'POP' && !clearing) {
      const queryParams = queryString.parse(location.search);

      if (typeof queryParams.q === 'string' && queryParams.q !== reduxQuery) {
        setQuery(truncateToUTF8Bytes(queryParams.q || '', 512));
      }
    }

    if (clearing) {
      setClearing(false);
    }
  }, [clearing, location.search, navigationType, reduxQuery]);

  const setUserQuery = useCallback(
    (q: string) => {
      // Algolia has a max query length of 512 bytes
      setQuery(truncateToUTF8Bytes(q, 512));
    },
    [setQuery],
  );

  const onLocationChange = (
    newSearchLocation: SearchLocation | null | undefined,
    shouldEnqueueSearch = true,
    setRemote = false,
  ) => {
    setSearchLocation(newSearchLocation);

    if (!newSearchLocation) {
      // @ts-expect-error TS(2345): Argument of type '{ boundingBoxE: null; boundingBo... Remove this comment to see the full error message
      dispatch(updateSearchLocation(EMPTY_SEARCH_LOCATION, true));
    }

    if (setRemote) {
      dispatch(
        setSearchFilter('locationTypeFacet', ['REMOTE', 'REMOTE_TEMPORARY']),
      );
    }

    if (shouldEnqueueSearch) {
      // @ts-expect-error TS(2322): Type 'string' is not assignable to type 'boolean |... Remove this comment to see the full error message
      setEnqueueSearch([true, query]);
    }
  };

  const search = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (stateQuery: any) => {
      const finalQuery = stateQuery;

      batch(() => {
        const mainSearchLocationAddress = reduxMainSearch.searchLocation
          ? // @ts-expect-error should latLongAddress exist on SearchLocation?
            reduxMainSearch.searchLocation.latLongAddress
          : undefined;

        const searchLocationAddress = searchLocation
          ? // @ts-expect-error TS(2339): Property 'latLongAddress' does not exist on type '... Remove this comment to see the full error message
            searchLocation.latLongAddress
          : undefined;

        const locationsMatch =
          mainSearchLocationAddress === searchLocationAddress;

        if (!locationsMatch) {
          if (searchLocation) {
            dispatch(updateSearchLocation(searchLocation, true)).then(() =>
              dispatch(setPageIndex(0, false)),
            );
          } else {
            dispatch(clearSearchLocation(true)).then(() =>
              dispatch(setPageIndex(0, false)),
            );
          }
        }

        if (reduxListingType && listingType !== reduxListingType) {
          dispatch(setSearchFilter('type', listingType));

          if (listingType === 'VOLOP') {
            setSearchFilter('actionTypeFacet', ['VOLOP']);
          }
        }

        dispatch(setSearchQuery(stateQuery, true));
      });

      const searchHome = listingSearchHome(listingType);
      const queryParams = queryString.parse(location.search);
      // FIXME: Next line has a bug that ESLint started to warn about when updating
      //        JS packages. It should be fixed, but it breaks a test.
      // eslint-disable-next-line valid-typeof
      const hasArticleSearch = typeof queryParams.pq !== undefined;

      if (searchHome && (!searchMode || (!queryParams.q && hasArticleSearch))) {
        if (finalQuery && finalQuery === reduxQuery) {
          navigate(`${searchHome}?q=${encodeURIComponent(finalQuery)}`, {
            state: {
              searchFrom,
            },
            replace: true,
          });
        } else {
          navigate(`${searchHome}?q=${encodeURIComponent(finalQuery)}`, {
            state: {
              searchFrom,
            },
          });
        }

        dispatch(setUserInitiatedSearch());
      }
    },
    [
      searchLocation,
      reduxMainSearch.searchLocation,
      listingType,
      location.search,
      searchMode,
      reduxListingType,
      dispatch,
      reduxQuery,
      navigate,
      searchFrom,
    ],
  );
  const redirectTo = location ? `${location.pathname}${location.search}` : '';

  const clickLogin = (e: SyntheticEvent) => {
    e.preventDefault();
    trackClicked('Log In Search Hero');
    dispatch(
      showModal('LOGIN', {
        redirectTo,
      }),
    );
  };

  const clickSignup = (e: SyntheticEvent) => {
    e.preventDefault();
    trackClicked('Sign Up Search Hero');
    dispatch(
      showModal('SIGNUP', {
        redirectTo,
      }),
    );
  };

  const redirectParam = queryString.stringify({
    to: redirectTo,
  });
  useEffect(() => {
    const [enqueued, searchQuery] = enqueueSearch;

    if (enqueued) {
      setEnqueueSearch([false, null]);
      search(searchQuery || '');
    }
  }, [enqueueSearch, search]);
  const clearSearchInput = useCallback(() => {
    setClearing(true);
    setUserQuery('');

    if (searchMode) {
      dispatch(setSearchQuery('', true));
    }
  }, [dispatch, setUserQuery, searchMode]);
  const selectedType =
    listingTypes.find((l) => l === listingType) ||
    (listingTypes.includes('JOB') ? 'JOB' : listingTypes[0]);

  return {
    listingTypes,
    selectedType,
    query,
    searchLocation,
    setListingType,
    setUserQuery,
    clearSearchInput,
    redirectParam,
    onLocationChange,
    search,
    clickLogin,
    clickSignup,
    showLogin: !user,
  };
}
